import { Turbo } from "@hotwired/turbo-rails"
import I18n from "../../i18n"
import ModalController from "./modal_controller"

const ERP_PATH = /^\/erp(\/|$)/
const DIALOG_ID = "erp-turbo-confirm"

// Same look as ERP::ConfirmDialog::Component and ERP::Button::Component (small size)
const DIALOG = "m-auto w-[min(440px,calc(100vw-32px))] rounded-[20px] border-0 bg-erp-surface p-0 text-erp-ink " +
  "shadow-[0_24px_64px_rgba(20,16,4,0.28)] backdrop:bg-[rgba(28,27,24,0.45)]"
const BUTTON = "inline-flex h-[38px] shrink-0 cursor-pointer items-center justify-center gap-2 whitespace-nowrap " +
  "rounded-full border px-3.5 text-[13px] font-bold transition-[background-color,border-color,transform] " +
  "duration-150 active:translate-y-px focus-visible:outline-2 focus-visible:outline-offset-2 " +
  "focus-visible:outline-erp-black"
const VARIANTS = {
  ghost: "border-erp-line-strong bg-erp-surface text-erp-ink hover:border-erp-ink",
  primary: "border-transparent bg-erp-black text-white hover:bg-black",
  danger: "border-transparent bg-erp-danger text-white hover:bg-[#961F10]"
}

// ERP::ConfirmDialog::Component: a confirmation dialog rendered by the page, opened from its trigger.
// The accept button submits its own form; the dialog closes as the form goes.
// Connects to data-controller="erp--confirm"
export default class extends ModalController {
  // On submit: close without preventDefault, which would cancel the submission
  dismiss() {
    if (this.dialogTarget.open) this.dialogTarget.close()
  }
}

// data-turbo-confirm in the ERP asks through the same dialog instead of window.confirm. The message is the
// title; the form or its submitter may add data-erp-confirm-text (explanation) and data-erp-confirm-accept
// (label of the accept button). A DELETE, or a submitter with data-erp-confirm-variant="danger", is confirmed
// with a danger button. Outside the ERP, Turbo keeps the native confirm.
export function confirmWithDialog(message, form, submitter) {
  const dialog = document.getElementById(DIALOG_ID) || buildDialog()
  const data = { ...form?.dataset, ...submitter?.dataset }
  fillDialog(dialog, { title: message, text: data.erpConfirmText, accept: data.erpConfirmAccept, danger: isDelete(form) || data.erpConfirmVariant === "danger" })

  dialog.returnValue = ""
  dialog.showModal()
  return new Promise((resolve) => {
    dialog.addEventListener("close", () => resolve(dialog.returnValue === "accept"), { once: true })
  })
}

Turbo.config.forms.confirm = (message, form, submitter) => {
  if (ERP_PATH.test(window.location.pathname)) return confirmWithDialog(message, form, submitter)

  return Promise.resolve(window.confirm(message))
}

function fillDialog(dialog, { title, text, accept, danger }) {
  dialog.querySelector("h2").textContent = title
  const details = dialog.querySelector("p")
  details.textContent = text || ""
  details.hidden = !text
  const [cancelButton, acceptButton] = dialog.querySelectorAll("button")
  cancelButton.textContent = I18n.t("erp.js.confirm.cancel")
  acceptButton.textContent = accept || I18n.t("erp.js.confirm.accept")
  acceptButton.className = `${BUTTON} ${danger ? VARIANTS.danger : VARIANTS.primary}`
}

// Built once per page; texts are set with textContent on each use.
function buildDialog() {
  const dialog = document.createElement("dialog")
  dialog.id = DIALOG_ID
  dialog.className = DIALOG
  dialog.setAttribute("aria-labelledby", `${DIALOG_ID}-title`)
  dialog.setAttribute("aria-describedby", `${DIALOG_ID}-text`)
  dialog.innerHTML = `
    <form method="dialog" class="flex flex-col gap-2.5 p-6">
      <h2 id="${DIALOG_ID}-title" class="text-lg font-extrabold"></h2>
      <p id="${DIALOG_ID}-text" class="text-sm leading-normal text-erp-muted"></p>
      <div class="mt-2.5 flex flex-wrap justify-end gap-2.5">
        <button type="submit" value="cancel" class="${BUTTON} ${VARIANTS.ghost}"></button>
        <button type="submit" value="accept"></button>
      </div>
    </form>`
  dialog.addEventListener("click", (event) => {
    if (event.target === dialog) dialog.close()
  })
  document.body.append(dialog)
  return dialog
}

function isDelete(form) {
  const method = form?.querySelector("input[name='_method']")?.value || form?.getAttribute("method") || ""
  return method.toLowerCase() === "delete"
}
